import { useEffect, useRef, useState } from "react";
import ReviewCard from "@/components/common/ReviewCard";

/**
 * Guest Love marquee — robust auto-scroll that always resettles with the active
 * card perfectly centered. We advance one card every ~3.5s; user interaction
 * (pointer hover with a real mouse, or a touch swipe) pauses the animation, and
 * a short idle resumes it. On touch devices we never pause from a stray tap —
 * only from an actual swipe gesture.
 *
 * Uses translate-based positioning (not scrollLeft) so the "center" is always
 * mathematically the active card — no drift into translucent gradient fades.
 */
export default function GuestLoveMarquee({ reviews, properties = [] }) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(null);

  const lookup = Object.fromEntries(properties.map((p) => [p.slug, p]));
  const N = reviews?.length || 0;

  useEffect(() => {
    if (!N || paused) return undefined;
    const id = setInterval(() => setActive((a) => (a + 1) % N), 3500);
    return () => clearInterval(id);
  }, [N, paused]);

  const advance = (dir) => setActive((a) => (a + dir + N) % N);

  // Only pause for REAL mouse enter (pointer fine) — touch devices emit a synthetic
  // mouseenter that never releases, which was freezing the auto-scroll on phones.
  const onPointerEnter = (e) => { if (e.pointerType === "mouse") setPaused(true); };
  const onPointerLeave = (e) => { if (e.pointerType === "mouse") setPaused(false); };

  const onTouchStart = (e) => { touchStartX.current = e.touches[0]?.clientX ?? null; };
  const onTouchEnd = (e) => {
    if (touchStartX.current != null) {
      const dx = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
      if (Math.abs(dx) > 40) {
        // Real swipe — briefly pause, then resume.
        setPaused(true);
        advance(dx < 0 ? 1 : -1);
        setTimeout(() => setPaused(false), 3000);
      }
    }
    touchStartX.current = null;
  };

  if (!N) return null;

  const slots = [-2, -1, 0, 1, 2];

  return (
    <div
      data-testid="guest-love-marquee"
      className="relative select-none overflow-hidden"
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="relative mx-auto flex h-[420px] max-w-6xl items-center justify-center sm:h-[400px]">
        {slots.map((offset) => {
          const idx = ((active + offset) % N + N) % N;
          const r = reviews[idx];
          const prop = lookup[r.property_slug];
          const isCenter = offset === 0;
          const abs = Math.abs(offset);
          const translatePct = offset * 66;
          const scale = isCenter ? 1 : abs === 1 ? 0.85 : 0.65;
          const opacity = isCenter ? 1 : abs === 1 ? 0.7 : 0.3;
          return (
            <div
              key={`slot-${offset}`}
              data-testid={`guest-love-slide${isCenter ? "-active" : ""}`}
              onClick={() => { if (!isCenter) advance(offset > 0 ? 1 : -1); }}
              className="absolute top-1/2 h-[360px] w-[84%] max-w-[420px] sm:h-[340px] sm:w-[60%] md:w-[42%]"
              style={{
                transform: `translate3d(calc(-50% + ${translatePct}%), -50%, 0) scale(${scale})`,
                left: "50%",
                opacity,
                zIndex: 10 - abs,
                cursor: isCenter ? "default" : "pointer",
                willChange: "transform, opacity",
                transition: "transform 700ms cubic-bezier(0.22, 0.61, 0.36, 1), opacity 500ms ease",
                backfaceVisibility: "hidden",
              }}
            >
              <ReviewCard r={r} propertyName={prop?.display_name} propertySlug={prop?.slug} />
            </div>
          );
        })}
      </div>

      <div className="mt-2 flex justify-center gap-1.5" data-testid="guest-love-dots">
        {reviews.map((r, i) => (
          <button
            key={r.id || i}
            onClick={() => { setActive(i); setPaused(true); setTimeout(() => setPaused(false), 2000); }}
            aria-label={`Review ${i + 1}`}
            className={`h-1.5 rounded-full transition-all ${i === active ? "w-6 bg-gold" : "w-1.5 bg-parchment/40 hover:bg-parchment/70"}`}
          />
        ))}
      </div>
    </div>
  );
}
